// =============================================================================
// Admin Wallets Page — cross-tenant wallet view
// -----------------------------------------------------------------------------
// Lists every org's wallet, lets the operator drill into one to see the
// transaction history, and exposes a manual-adjustment form gated by
// `wallet:adjust`. Manual adjustments always emit an AuditLog row server-
// side so support / compliance can trace operator activity.
// =============================================================================

import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router';
import { useApiClient } from '@tkpod/api-client';
import {
  formatMoneyDto,
  type MoneyDto,
  type WalletBalanceDto,
  type WalletStoreSpendDto,
  type WalletTransactionDto,
  type WalletTransactionListResponse,
} from '@tkpod/shared';
import { Button, Card, CardHeader, Modal, ModalFooter, Spinner } from '@tkpod/ui';

interface AdminWalletRow {
  walletId: string;
  organizationId: string;
  organizationName: string;
  accountKind: 'MERCHANT' | 'HOUSE';
  balance: MoneyDto;
  heldAmount: MoneyDto;
  availableBalance: MoneyDto;
  isFrozen: boolean;
  freezeReason: string | null;
  autoRecharge: boolean;
  lastReconciledAt: string | null;
  updatedAt: string;
}

interface AdminWalletListResponse {
  items: AdminWalletRow[];
  page: number;
  limit: number;
  total: number;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function dollarsStringToCents(input: string, allowNegative = true): bigint | null {
  const trimmed = input.trim();
  const re = allowNegative ? /^-?\d+(\.\d{1,2})?$/ : /^\d+(\.\d{1,2})?$/;
  if (!re.test(trimmed)) return null;
  const negative = trimmed.startsWith('-');
  const abs = negative ? trimmed.slice(1) : trimmed;
  const [whole, frac = ''] = abs.split('.');
  const padded = frac.padEnd(2, '0');
  try {
    const cents = BigInt(whole) * 100n + BigInt(padded);
    return negative ? -cents : cents;
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Top-level page (list view)
// ---------------------------------------------------------------------------

export function AdminWalletsPage() {
  const [params, setParams] = useSearchParams();
  const selectedOrg = params.get('org');

  if (selectedOrg) {
    return (
      <AdminWalletDetail
        organizationId={selectedOrg}
        onBack={() => {
          const next = new URLSearchParams(params);
          next.delete('org');
          setParams(next, { replace: true });
        }}
      />
    );
  }

  return <AdminWalletsList />;
}

function AdminWalletsList() {
  const api = useApiClient();
  const [params, setParams] = useSearchParams();
  const page = Math.max(1, Number.parseInt(params.get('page') ?? '1', 10) || 1);
  const search = params.get('search') ?? '';
  const onlyFrozen = params.get('frozen') === 'true';

  const query = useQuery<AdminWalletListResponse>({
    queryKey: ['admin', 'wallets', { page, search, onlyFrozen }],
    queryFn: () => {
      const qs = new URLSearchParams();
      qs.set('page', String(page));
      qs.set('limit', '25');
      if (search) qs.set('search', search);
      if (onlyFrozen) qs.set('frozen', 'true');
      return api.get<AdminWalletListResponse>(`/api/v1/admin/wallets?${qs}`);
    },
  });

  function setSearch(value: string) {
    const next = new URLSearchParams(params);
    if (value) next.set('search', value);
    else next.delete('search');
    next.delete('page');
    setParams(next, { replace: true });
  }

  function setFrozen(value: boolean) {
    const next = new URLSearchParams(params);
    if (value) next.set('frozen', 'true');
    else next.delete('frozen');
    next.delete('page');
    setParams(next, { replace: true });
  }

  function setPage(value: number) {
    const next = new URLSearchParams(params);
    next.set('page', String(value));
    setParams(next, { replace: true });
  }

  return (
    <Card padding="none">
      <div className="flex flex-col gap-3 border-b border-border px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-lg font-semibold text-text-primary">Wallets</h1>
          <p className="text-xs text-text-tertiary">
            Cross-tenant wallet view. Click an organization to inspect the
            ledger or apply a manual adjustment.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <input
            type="text"
            placeholder="Search organization…"
            defaultValue={search}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                setSearch((e.target as HTMLInputElement).value);
              }
            }}
            className="w-64 rounded-md border border-border bg-surface px-3 py-1.5 text-sm focus:border-brand-600 focus:outline-none"
          />
          <label className="flex items-center gap-2 text-sm text-text-secondary">
            <input
              type="checkbox"
              checked={onlyFrozen}
              onChange={(e) => setFrozen(e.target.checked)}
              className="h-4 w-4 rounded border-border text-brand-600 focus:ring-brand-600"
            />
            Only frozen
          </label>
        </div>
      </div>

      {query.isLoading ? (
        <div className="flex h-32 items-center justify-center">
          <Spinner />
        </div>
      ) : query.isError ? (
        <div className="space-y-2 px-5 py-6">
          <p className="text-sm text-rose-600">
            {query.error instanceof Error
              ? query.error.message
              : 'Unable to load wallets.'}
          </p>
          <Button variant="secondary" onClick={() => query.refetch()}>
            Retry
          </Button>
        </div>
      ) : !query.data?.items.length ? (
        <div className="px-5 py-12 text-center text-sm text-text-secondary">
          No wallets match your filters.
        </div>
      ) : (
        <>
          <table className="w-full text-left text-sm">
            <thead className="border-b border-border bg-surface-tertiary text-xs uppercase tracking-wide text-text-tertiary">
              <tr>
                <th className="px-5 py-2 font-semibold">Organization</th>
                <th className="px-5 py-2 font-semibold">Type</th>
                <th className="px-5 py-2 text-right font-semibold">Balance</th>
                <th className="px-5 py-2 text-right font-semibold">Available</th>
                <th className="px-5 py-2 text-right font-semibold">Held</th>
                <th className="px-5 py-2 font-semibold">Auto-recharge</th>
                <th className="px-5 py-2 font-semibold">Status</th>
                <th className="px-5 py-2 font-semibold">Updated</th>
              </tr>
            </thead>
            <tbody>
              {query.data.items.map((wallet) => (
                <tr
                  key={wallet.walletId}
                  className="cursor-pointer border-b border-border/60 hover:bg-surface-tertiary/60"
                  onClick={() => {
                    const next = new URLSearchParams(params);
                    next.set('org', wallet.organizationId);
                    setParams(next);
                  }}
                >
                  <td className="px-5 py-3 align-top">
                    <p className="font-medium text-text-primary">
                      {wallet.organizationName}
                    </p>
                    <p className="text-xs text-text-tertiary">
                      {wallet.organizationId}
                    </p>
                  </td>
                  <td className="px-5 py-3 align-top">
                    {wallet.accountKind === 'HOUSE' ? (
                      <span className="inline-flex items-center rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-amber-800">
                        House
                      </span>
                    ) : (
                      <span className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-slate-700">
                        Merchant
                      </span>
                    )}
                  </td>
                  <td className="px-5 py-3 text-right align-top text-text-primary">
                    {formatMoneyDto(wallet.balance)}
                  </td>
                  <td className="px-5 py-3 text-right align-top text-text-primary">
                    {formatMoneyDto(wallet.availableBalance)}
                  </td>
                  <td className="px-5 py-3 text-right align-top text-text-secondary">
                    {formatMoneyDto(wallet.heldAmount)}
                  </td>
                  <td className="px-5 py-3 align-top text-text-secondary">
                    {wallet.autoRecharge ? 'On' : 'Off'}
                  </td>
                  <td className="px-5 py-3 align-top">
                    {wallet.isFrozen ? (
                      <span
                        className="inline-flex items-center rounded-full bg-rose-50 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-rose-700"
                        title={wallet.freezeReason ?? 'Frozen'}
                      >
                        Frozen
                      </span>
                    ) : (
                      <span className="inline-flex items-center rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-emerald-700">
                        Active
                      </span>
                    )}
                  </td>
                  <td className="px-5 py-3 align-top text-xs text-text-tertiary">
                    {new Date(wallet.updatedAt).toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="flex items-center justify-between border-t border-border px-5 py-3 text-xs text-text-secondary">
            <span>
              Page {page} • {query.data.total} wallet
              {query.data.total === 1 ? '' : 's'} total
            </span>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="ghost"
                disabled={page <= 1}
                onClick={() => setPage(page - 1)}
              >
                Previous
              </Button>
              <Button
                size="sm"
                variant="ghost"
                disabled={
                  page * query.data.limit >= query.data.total
                }
                onClick={() => setPage(page + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        </>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Detail view — single org's wallet
// ---------------------------------------------------------------------------

function AdminWalletDetail({
  organizationId,
  onBack,
}: {
  organizationId: string;
  onBack: () => void;
}) {
  const api = useApiClient();
  const queryClient = useQueryClient();
  const [adjustOpen, setAdjustOpen] = useState(false);

  const balanceQuery = useQuery<WalletBalanceDto>({
    queryKey: ['admin', 'wallet', organizationId, 'balance'],
    queryFn: () =>
      api.get<WalletBalanceDto>(`/api/v1/admin/wallets/${organizationId}`),
  });

  const txQuery = useQuery<WalletTransactionListResponse>({
    queryKey: ['admin', 'wallet', organizationId, 'transactions'],
    queryFn: () =>
      api.get<WalletTransactionListResponse>(
        `/api/v1/admin/wallets/${organizationId}/transactions?limit=50&includeHolds=true`,
      ),
  });

  const spendQuery = useQuery<{ items: WalletStoreSpendDto[] }>({
    queryKey: ['admin', 'wallet', organizationId, 'store-spend'],
    queryFn: () =>
      api.get<{ items: WalletStoreSpendDto[] }>(
        `/api/v1/admin/wallets/${organizationId}/store-spend`,
      ),
  });

  const storeNameById = useMemo(() => {
    const map = new Map<string | null, string>();
    for (const row of spendQuery.data?.items ?? []) {
      map.set(row.connectionId, row.storeName);
    }
    return map;
  }, [spendQuery.data]);

  const adjustMutation = useMutation({
    mutationFn: (body: {
      amountCents: string;
      reason: string;
      correlationId?: string;
    }) =>
      api.post<{ transactionId: string; balanceAfterCents: string }>(
        `/api/v1/admin/wallets/${organizationId}/adjust`,
        body,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['admin', 'wallet', organizationId],
      });
      setAdjustOpen(false);
    },
  });

  const balance = balanceQuery.data;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <Button variant="ghost" onClick={onBack}>
          ← Back to wallets
        </Button>
        <Link
          to={`/tenants/${organizationId}`}
          className="text-sm font-medium text-brand-600 hover:underline"
        >
          View organization →
        </Link>
      </div>

      <Card>
        <CardHeader title="Wallet snapshot" />
        <div className="space-y-4 px-4 pb-5 sm:px-5">
          {balanceQuery.isLoading ? (
            <Spinner />
          ) : balanceQuery.isError || !balance ? (
            <p className="text-sm text-rose-600">
              {balanceQuery.error instanceof Error
                ? balanceQuery.error.message
                : 'Unable to load wallet.'}
            </p>
          ) : (
            <>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <Tile label="Available" money={balance.availableBalance} emphasis />
                <Tile label="Total" money={balance.balance} />
                <Tile label="Held" money={balance.heldAmount} />
              </div>
              <p className="text-xs text-text-tertiary">
                Wallet ID: {balance.walletId}
                {balance.lastReconciledAt && (
                  <span className="ml-3">
                    Last reconciled:{' '}
                    {new Date(balance.lastReconciledAt).toLocaleString()}
                  </span>
                )}
              </p>
              {balance.isFrozen && (
                <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-900">
                  Wallet frozen
                  {balance.freezeReason ? `: ${balance.freezeReason}` : '.'}
                </div>
              )}
              {spendQuery.data?.items.length ? (
                <div>
                  <p className="mb-2 text-xs uppercase tracking-wide text-text-tertiary">
                    Store spend
                  </p>
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                    {spendQuery.data.items.map((row) => (
                      <div
                        key={row.connectionId ?? 'manual'}
                        className="rounded-md border border-border px-3 py-2"
                      >
                        <p className="text-sm font-medium text-text-primary">
                          {row.storeName}
                        </p>
                        <p className="text-xs text-text-secondary">
                          {formatMoneyDto(row.netSpent)} · {row.orderCount}{' '}
                          order{row.orderCount === 1 ? '' : 's'}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}
              <div className="flex flex-wrap gap-2">
                <Button onClick={() => setAdjustOpen(true)}>
                  Manual adjustment
                </Button>
              </div>
            </>
          )}
        </div>
      </Card>

      <Card padding="none">
        <div className="border-b border-border px-5 py-3">
          <h2 className="text-base font-semibold text-text-primary">
            Recent activity
          </h2>
        </div>
        {txQuery.isLoading ? (
          <div className="flex h-32 items-center justify-center">
            <Spinner />
          </div>
        ) : txQuery.isError ? (
          <p className="px-5 py-6 text-sm text-rose-600">
            {txQuery.error instanceof Error
              ? txQuery.error.message
              : 'Unable to load transactions.'}
          </p>
        ) : !txQuery.data?.items.length ? (
          <p className="px-5 py-12 text-center text-sm text-text-secondary">
            No wallet movements yet.
          </p>
        ) : (
          <table className="w-full text-left text-sm">
            <thead className="border-b border-border bg-surface-tertiary text-xs uppercase tracking-wide text-text-tertiary">
              <tr>
                <th className="px-5 py-2 font-semibold">Date</th>
                <th className="px-5 py-2 font-semibold">Type</th>
                <th className="px-5 py-2 font-semibold">Reason</th>
                <th className="px-5 py-2 font-semibold">Store</th>
                <th className="px-5 py-2 font-semibold">Description</th>
                <th className="px-5 py-2 text-right font-semibold">Amount</th>
                <th className="px-5 py-2 text-right font-semibold">Balance</th>
              </tr>
            </thead>
            <tbody>
              {txQuery.data.items.map((tx: WalletTransactionDto) => {
                const credit = !tx.amount.amountCents.startsWith('-');
                return (
                  <tr key={tx.id} className="border-b border-border/60">
                    <td className="px-5 py-3 align-top text-text-secondary">
                      {new Date(tx.createdAt).toLocaleString()}
                    </td>
                    <td className="px-5 py-3 align-top text-text-secondary">
                      {tx.type}
                    </td>
                    <td className="px-5 py-3 align-top text-text-secondary">
                      {tx.reason ?? '—'}
                    </td>
                    <td className="px-5 py-3 align-top text-text-secondary">
                      {tx.connectionId
                        ? (storeNameById.get(tx.connectionId) ?? 'Store')
                        : (storeNameById.get(null) ?? 'Manual / Unassigned')}
                    </td>
                    <td className="px-5 py-3 align-top text-text-secondary">
                      {tx.description ?? '—'}
                    </td>
                    <td
                      className={`px-5 py-3 text-right align-top font-medium ${
                        credit
                          ? 'text-emerald-600 dark:text-emerald-400'
                          : 'text-rose-600 dark:text-rose-400'
                      }`}
                    >
                      {formatMoneyDto(tx.amount, { sign: 'always' })}
                    </td>
                    <td className="px-5 py-3 text-right align-top text-text-primary">
                      {formatMoneyDto(tx.balanceAfter)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </Card>

      <ManualAdjustModal
        open={adjustOpen}
        onClose={() => setAdjustOpen(false)}
        onSubmit={(payload) => adjustMutation.mutate(payload)}
        loading={adjustMutation.isPending}
        error={
          adjustMutation.error instanceof Error
            ? adjustMutation.error.message
            : null
        }
      />
    </div>
  );
}

function Tile({
  label,
  money,
  emphasis,
}: {
  label: string;
  money: MoneyDto;
  emphasis?: boolean;
}) {
  return (
    <div className="rounded-lg border border-border bg-surface-tertiary px-4 py-3">
      <p className="text-xs uppercase tracking-wide text-text-tertiary">
        {label}
      </p>
      <p
        className={`mt-1 ${
          emphasis
            ? 'text-2xl font-semibold text-text-primary'
            : 'text-lg font-medium text-text-primary'
        }`}
      >
        {formatMoneyDto(money)}
      </p>
    </div>
  );
}

function ManualAdjustModal({
  open,
  onClose,
  onSubmit,
  loading,
  error,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (payload: {
    amountCents: string;
    reason: string;
    correlationId?: string;
  }) => void;
  loading: boolean;
  error: string | null;
}) {
  const [direction, setDirection] = useState<'credit' | 'debit'>('credit');
  const [amount, setAmount] = useState('0.00');
  const [reason, setReason] = useState('');
  const [correlationId, setCorrelationId] = useState('');
  const [validation, setValidation] = useState<string | null>(null);

  const cents = useMemo(() => {
    const positive = dollarsStringToCents(amount, false);
    if (positive === null) return null;
    return direction === 'credit' ? positive : -positive;
  }, [amount, direction]);

  function handleSubmit() {
    setValidation(null);
    if (cents === null || cents === 0n) {
      setValidation('Enter a non-zero USD amount.');
      return;
    }
    if (reason.trim().length < 3) {
      setValidation('Reason must be at least 3 characters.');
      return;
    }
    onSubmit({
      amountCents: cents.toString(),
      reason: reason.trim(),
      correlationId: correlationId.trim() || undefined,
    });
  }

  return (
    <Modal
      open={open}
      onClose={loading ? () => {} : onClose}
      title="Manual wallet adjustment"
      description="Credits the wallet (positive) or debits it (negative). Both the wallet ledger AND an audit-log row are written for every adjustment — choose the reason carefully."
      size="md"
    >
      <div className="space-y-4">
        <div className="flex gap-3">
          {(['credit', 'debit'] as const).map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => setDirection(d)}
              disabled={loading}
              className={`flex-1 rounded-md border px-3 py-2 text-sm font-medium transition ${
                direction === d
                  ? 'border-brand-600 bg-brand-50 text-brand-700'
                  : 'border-border text-text-primary hover:bg-surface-tertiary'
              }`}
            >
              {d === 'credit' ? 'Credit (add funds)' : 'Debit (remove funds)'}
            </button>
          ))}
        </div>

        <div>
          <label
            htmlFor="adj-amount"
            className="mb-1 block text-xs font-medium uppercase tracking-wide text-text-tertiary"
          >
            Amount (USD)
          </label>
          <input
            id="adj-amount"
            type="text"
            inputMode="decimal"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            disabled={loading}
            className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:border-brand-600 focus:outline-none"
          />
        </div>

        <div>
          <label
            htmlFor="adj-reason"
            className="mb-1 block text-xs font-medium uppercase tracking-wide text-text-tertiary"
          >
            Reason (visible in audit log)
          </label>
          <textarea
            id="adj-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            disabled={loading}
            placeholder="Refund issue ZD-1234 — duplicate charge"
            className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:border-brand-600 focus:outline-none"
          />
        </div>

        <div>
          <label
            htmlFor="adj-correlation"
            className="mb-1 block text-xs font-medium uppercase tracking-wide text-text-tertiary"
          >
            Correlation ID (optional)
          </label>
          <input
            id="adj-correlation"
            type="text"
            value={correlationId}
            onChange={(e) => setCorrelationId(e.target.value)}
            disabled={loading}
            placeholder="ZD-1234"
            className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:border-brand-600 focus:outline-none"
          />
        </div>

        {(validation || error) && (
          <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-900">
            {validation ?? error}
          </div>
        )}
      </div>

      <ModalFooter>
        <Button variant="ghost" onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} disabled={loading}>
          {loading
            ? 'Applying…'
            : `Apply ${direction === 'credit' ? '+' : '-'}${amount}`}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
